/** Unified stage routing for ticket, visa, and tour inquiries. */
import { prepareFlowSteps } from './terminal-flow-merge.js';
import { injectCustomSteps, getCustomStepsForFlow } from './terminal-flow-custom.js';
import { pickCustomStageAnswers } from './terminal-flow-handlers.js';
import {
  ticketStageSteps,
  buildStatusPatchBody as buildTicketStatusPatchBody,
  validateStageAnswers as validateTicketStageAnswers,
  statusDisplayName,
  leadHasDueBalance,
} from './assistant-ticket-stages.js';
import {
  visaStageSteps,
  buildVisaStatusPatchBody,
  validateVisaStageAnswers,
} from './assistant-visa-stages.js';
import {
  tourStageSteps,
  buildTourStatusPatchBody,
  validateTourStageAnswers,
} from './assistant-tour-stages.js';

export { statusDisplayName, leadHasDueBalance };

export function activeStageSteps(type, status, lead, answers = {}) {
  let steps = [];
  if (type === 'visa') steps = visaStageSteps(status, lead);
  else if (type === 'tour') steps = tourStageSteps(status, lead, answers);
  else steps = ticketStageSteps(status, lead);
  steps = steps.filter((step) => !step.when || step.when(answers, lead));
  const flowId = `${type}.stage.${status}`;
  return prepareFlowSteps(flowId, injectCustomSteps(flowId, steps, { mode: 'stage', lead, stageAnswers: answers }));
}

function stepSavedValue(step, answers, lead) {
  if (answers[step.key] != null && answers[step.key] !== '') return answers[step.key];
  if (typeof step.default === 'function') {
    const def = step.default(answers, lead);
    if (def != null && def !== '') return def;
  }
  return null;
}

/** Fill unanswered steps from defaults (e.g. advance paid today) before save validation. */
export function applyUnansweredStepDefaults(steps, answers, lead) {
  if (!Array.isArray(steps)) return;
  for (const st of steps) {
    if (answers[st.key] != null && answers[st.key] !== '') continue;
    if (typeof st.default !== 'function') continue;
    const def = st.default(answers, lead);
    if (def == null || def === '') continue;
    answers[st.key] = st.parse ? st.parse(String(def), answers, lead) : def;
  }
}

/** Required stage steps that do not yet have saved values on the lead. */
export function missingStageSteps(type, status, lead, answers = {}) {
  return activeStageSteps(type, status, lead, answers).filter((step) => {
    if (step.optional) return false;
    if (typeof step.isSaved === 'function') return !step.isSaved(lead);
    return stepSavedValue(step, answers, lead) == null;
  });
}

export function stageStepsComplete(type, status, lead, answers = {}) {
  return missingStageSteps(type, status, lead, answers).length === 0;
}

export function buildStatusPatchBody(type, status, lead, answers = {}) {
  const flowId = `${type}.stage.${status}`;
  let body;
  if (type === 'visa') body = buildVisaStatusPatchBody(status, lead, answers);
  else if (type === 'tour') body = buildTourStatusPatchBody(status, lead, answers);
  else body = buildTicketStatusPatchBody(type, status, lead, answers);

  const custom = pickCustomStageAnswers(flowId, answers, getCustomStepsForFlow(flowId));
  if (Object.keys(custom).length) {
    body.extra = body.extra || {};
    body.extra.custom_steps = { ...(body.extra.custom_steps || {}), ...custom };
  }
  return body;
}

export function validateStageAnswers(type, status, lead, answers = {}) {
  if (type === 'visa') return validateVisaStageAnswers(status, lead, answers);
  if (type === 'tour') return validateTourStageAnswers(status, lead, answers);
  return validateTicketStageAnswers(type, status, lead, answers);
}

export {
  parseAssistantDatetime,
  parseAssistantDate,
} from './assistant-ticket-stages.js';
